import { ArrowRight, AtSign, Eye, EyeOff, LockKeyhole } from "lucide-react";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../store";
import { clearState, login } from "../../../Redux/custmoer_auth/slice";
import {
    signInContent,
    signInRoutes,
    signInSocialLinks,
    signInValidationMessages,
} from "./SignInData";
import { SignInErrors } from "./SignInTypes";
import { LOCATION_GATE_SESSION_KEY } from "../../globalcomponents/LocationSetupModal/LocationSetupData";
import "./SignInPage.css";

const SignInPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { user, authloading, autherror, success } = useSelector(
        (state: RootState) => state.customerAuth
    );

    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<SignInErrors>({});
    const toastShown = useRef(false);

    useEffect(() => {
        if (user && success && !toastShown.current) {
            toastShown.current = true;
            toast.success("Success! You are now logged in.", {
                toastId: "signin-success",
            });
            sessionStorage.setItem(LOCATION_GATE_SESSION_KEY, "1");
            dispatch(clearState());
            navigate("/HomePage");
        }
    }, [user, success, navigate, dispatch]);

    // Cleanup on unmount only
    useEffect(() => {
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    useEffect(() => {
        if (autherror) {
            setErrors((prev) => ({ ...prev, api: autherror }));
            toast.error(autherror, { toastId: "signin-error" });
        }
    }, [autherror]);

    const validateForm = () => {
        const nextErrors: SignInErrors = {};
        const trimmedIdentifier = identifier.trim();

        if (!trimmedIdentifier) {
            nextErrors.identifier = signInValidationMessages.customerIdRequired;
        } else {
            const isEmail = trimmedIdentifier.includes("@");
            const isMobile = /^\d+$/.test(trimmedIdentifier);
            const isCustomerId = trimmedIdentifier.toUpperCase().startsWith("S");

            if (isEmail) {
                const atCount = (trimmedIdentifier.match(/@/g) || []).length;
                const digitCount = (trimmedIdentifier.match(/\d/g) || []).length;

                if (atCount !== 1) {
                    nextErrors.identifier = "Email must contain exactly one '@'";
                } else {
                    const [, domainPart] = trimmedIdentifier.split("@");
                    const domainDotCount = (domainPart?.match(/\./g) || []).length;

                    if (domainDotCount < 1 || domainDotCount > 2) {
                        nextErrors.identifier =
                            "Domain must have 1 or 2 dots (e.g. .com or .co.in)";
                    } else if (trimmedIdentifier.length > 30) {
                        nextErrors.identifier = "Email cannot exceed 30 characters";
                    } else if (digitCount > 6) {
                        nextErrors.identifier =
                            "Email cannot contain more than 6 digits";
                    } else if (
                        !/^[a-zA-Z0-9.]+@[a-zA-Z0-9]+(\.[a-zA-Z0-9]+){1,2}$/.test(
                            trimmedIdentifier
                        )
                    ) {
                        nextErrors.identifier =
                            "Invalid email format (only '@' and '.' allowed as special characters)";
                    }
                }
            } else if (isMobile) {
                if (trimmedIdentifier.length !== 10) {
                    nextErrors.identifier = "Mobile number must be exactly 10 digits";
                }
            } else if (isCustomerId) {
                const alphabetsCount = (
                    trimmedIdentifier.match(/[a-zA-Z]/g) || []
                ).length;
                if (trimmedIdentifier.length > 11) {
                    nextErrors.identifier = "Customer ID cannot exceed 11 characters";
                } else if (/[^a-zA-Z0-9]/.test(trimmedIdentifier)) {
                    nextErrors.identifier =
                        "Customer ID cannot contain special characters";
                } else if (alphabetsCount > 2) {
                    nextErrors.identifier =
                        "Customer ID allows only one additional alphabet after 'S'";
                }
            } else {
                nextErrors.identifier =
                    "Invalid input — must be Email, 10-digit Mobile, or Customer ID starting with 'S'";
            }
        }

        if (!password) {
            nextErrors.password = signInValidationMessages.passwordRequired;
        } else if (password.length > 15) {
            nextErrors.password = "Password cannot exceed 15 characters";
        } else if (
            !/^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-])/.test(
                password
            )
        ) {
            nextErrors.password =
                "Password must contain uppercase, lowercase, a number, and a special character";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\s/g, "");

        if (/^\d+$/.test(value)) {
            // Mobile: digits only, max 10
            value = value.slice(0, 10);
        } else if (value.toUpperCase().startsWith("S") && !value.includes("@")) {
            // Customer ID: S + one random alphabet anywhere + digits, max 11
            value = value.replace(/[^a-zA-Z0-9]/g, "");
            if (value.length > 0) {
                const rest = value.slice(1);
                let alphaCount = 0;
                const filteredRest = rest.replace(/[a-zA-Z]/g, (char) => {
                    if (alphaCount < 1) {
                        alphaCount++;
                        return char;
                    }
                    return "";
                });
                value = "S" + filteredRest;
            }
            if (value.length > 11) value = value.slice(0, 11);
        } else {
            // Email: only alphanumeric + "@" + "." allowed
            value = value.replace(/[^a-zA-Z0-9@.]/g, "");

            // Enforce single "@"
            const firstAt = value.indexOf("@");
            if (firstAt !== -1) {
                value =
                    value.slice(0, firstAt + 1) +
                    value.slice(firstAt + 1).replace(/@/g, "");
            }

            // Enforce max 2 dots
            let dotCount = 0;
            value = value.replace(/\./g, (dot) => (dotCount++ < 2 ? dot : ""));

            // Enforce max 6 numeric digits
            let digitCount = 0;
            value = value.replace(/\d/g, (d) => (digitCount++ < 6 ? d : ""));

            // Max 30 characters
            if (value.length > 30) value = value.slice(0, 30);
        }

        setIdentifier(value);
        setErrors((current) => ({
            ...current,
            identifier: undefined,
            customerId: undefined,
            api: undefined,
        }));
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/\s/g, "");
        if (value.length > 15) value = value.slice(0, 15);
        setPassword(value);
        setErrors((current) => ({
            ...current,
            password: undefined,
            api: undefined,
        }));
    };

    const handleSignIn = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateForm() || authloading) {
            return;
        }

        dispatch(
            login({
                identifier: identifier.trim(),
                password,
            })
        );
    };

    const displayIdentifierError = errors.identifier || errors.customerId;

    return (
        <main className="signin-page">
            <div className="signin-shell">
                <section className="signin-left">
                    <div className="signin-left-content">
                        <div className="signin-brand-section">
                            <button
                                type="button"
                                className="signin-brand"
                                onClick={() => navigate(signInRoutes.home)}
                                aria-label="Go to Salar home"
                            >
                                <img
                                    src="/assets/images/logos/salarlogo.png"
                                    alt={signInContent.brandAlt}
                                    onError={(e) => {
                                        (e.currentTarget as HTMLImageElement).src =
                                            "./assets/img/salarlogo.png";
                                    }}
                                />
                            </button>
                            <div className="signin-brand-accent" aria-hidden="true">
                                <span /> <b /><span />
                            </div>
                        </div>

                        <header className="signin-heading">
                            <h1>{signInContent.title}</h1>
                            <p>{signInContent.subtitle}</p>
                        </header>

                        <form className="signin-form" onSubmit={handleSignIn} noValidate>
                            {errors.api && (
                                <div
                                    className="signin-error"
                                    style={{
                                        marginBottom: "12px",
                                        textAlign: "center",
                                    }}
                                >
                                    {errors.api}
                                </div>
                            )}

                            <div className="signin-field-group">
                                <div
                                    className={`signin-field ${
                                        displayIdentifierError ? "has-error" : ""
                                    }`}
                                >
                                    <AtSign size={18} strokeWidth={1.9} />
                                    <input
                                        type="text"
                                        name="identifier"
                                        value={identifier}
                                        onChange={handleIdentifierChange}
                                        onKeyDown={(e) => {
                                            if (e.key === " ") e.preventDefault();
                                        }}
                                        onBeforeInput={(e: any) => {
                                            if (e.data === ". " || e.data === " ") {
                                                e.preventDefault();
                                            }
                                        }}
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        disabled={authloading}
                                        placeholder={signInContent.customerIdPlaceholder}
                                        autoComplete="username"
                                        aria-label={signInContent.customerIdPlaceholder}
                                    />
                                </div>
                                {displayIdentifierError && (
                                    <span className="signin-error">
                                        {displayIdentifierError}
                                    </span>
                                )}
                            </div>

                            <div className="signin-field-group">
                                <div
                                    className={`signin-field ${
                                        errors.password ? "has-error" : ""
                                    }`}
                                >
                                    <LockKeyhole size={18} strokeWidth={1.9} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        onKeyDown={(e) => {
                                            if (e.key === " ") e.preventDefault();
                                        }}
                                        onBeforeInput={(e: any) => {
                                            if (e.data === ". " || e.data === " ") {
                                                e.preventDefault();
                                            }
                                        }}
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        maxLength={15}
                                        disabled={authloading}
                                        placeholder={signInContent.passwordPlaceholder}
                                        autoComplete="current-password"
                                        aria-label={signInContent.passwordPlaceholder}
                                    />
                                    <button
                                        type="button"
                                        className="signin-password-toggle"
                                        onClick={() =>
                                            setShowPassword((current) => !current)
                                        }
                                        aria-label={
                                            showPassword
                                                ? "Hide password"
                                                : "Show password"
                                        }
                                    >
                                        {showPassword ? (
                                            <EyeOff size={18} strokeWidth={1.9} />
                                        ) : (
                                            <Eye size={18} strokeWidth={1.9} />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <span className="signin-error">
                                        {errors.password}
                                    </span>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="signin-submit"
                                disabled={authloading}
                            >
                                <span>
                                    {authloading
                                        ? signInContent.signingInLabel
                                        : signInContent.signInLabel}
                                </span>
                                <ArrowRight size={17} strokeWidth={2} />
                            </button>

                            <div className="signin-signup-row">
                                <span>{signInContent.signUpPrompt}</span>
                                <button
                                    type="button"
                                    onClick={() => navigate(signInRoutes.signUp)}
                                >
                                    {signInContent.signUpLabel}
                                </button>
                            </div>

                            <button
                                type="button"
                                className="signin-forgot"
                                onClick={() =>
                                    navigate(signInRoutes.forgotPassword)
                                }
                            >
                                {signInContent.forgotPasswordLabel}
                            </button>
                        </form>

                        <div className="signin-social">
                            <span>{signInContent.socialTitle}</span>
                            <div className="signin-social-row">
                                {signInSocialLinks.map((social) => (
                                    <a
                                        key={social.id}
                                        href={social.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={social.label}
                                        title={social.label}
                                    >
                                        <i className={social.iconClass} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="signin-right" aria-label="Salar shopping">
                    <div className="signin-visual">
                        <img
                            src="/assets/img/register/signin-2.jpg"
                            alt={signInContent.heroAlt}
                            onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                    "../../assets/images/images/signin-2.jpg";
                            }}
                        />
                        <div className="signin-visual-overlay" />
                        <span className="signin-orbit signin-orbit-one" />
                        <span className="signin-orbit signin-orbit-two" />
                        <span className="signin-shine" />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default SignInPage;
